import { useCallback, useState } from 'react';
import {
  CustomAttributesEntity,
  getCustomAttributesConfig,
} from 'src/graphql/entities/custom-attributes/custom-attributes-config/custom-attributes-config';
import {
  CustomAttributeValue,
  NormalizedCustomAttributeDefinition,
} from 'src/graphql/entities/custom-attributes/custom-attributes.types';

interface UseCustomAttributeSavingParams {
  entity: CustomAttributesEntity;
  entityId: string;
}

interface UseCustomAttributeSavingResult {
  setFieldValue: (
    definition: NormalizedCustomAttributeDefinition,
    value: CustomAttributeValue,
  ) => Promise<void>;
  savingKeys: Set<string>;
  errorByKey: Map<string, string>;
}

/**
 * persists a single custom-attribute value to the backend and tracks
 * in-flight saves and per-field errors so the UI can show spinners
 * and inline error messages while a save is pending.
 */
export function useCustomAttributeSaving({
  entity,
  entityId,
}: UseCustomAttributeSavingParams): UseCustomAttributeSavingResult {
  const { useUpdateStoredAttributes } = getCustomAttributesConfig(entity);
  const { setFieldValue: setFieldValueRaw } = useUpdateStoredAttributes();

  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [errorByKey, setErrorByKey] = useState<Map<string, string>>(new Map());

  const setFieldValue = useCallback(
    async (definition: NormalizedCustomAttributeDefinition, value: CustomAttributeValue) => {
      setSavingKeys((current) => {
        const next = new Set(current);
        next.add(definition.key);
        return next;
      });
      setErrorByKey((current) => {
        if (!current.has(definition.key)) return current;
        const next = new Map(current);
        next.delete(definition.key);
        return next;
      });

      try {
        await setFieldValueRaw({
          entityId,
          key: definition.key,
          type: definition.type,
          value,
          defaultValue: definition.defaultValue,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save';
        setErrorByKey((current) => {
          const next = new Map(current);
          next.set(definition.key, message);
          return next;
        });
      } finally {
        setSavingKeys((current) => {
          const next = new Set(current);
          next.delete(definition.key);
          return next;
        });
      }
    },
    [entityId, setFieldValueRaw],
  );

  return { setFieldValue, savingKeys, errorByKey };
}
